import { useEffect } from "react";
import { LANGUAGE_PAIRS } from "../../../shared/localePaths";

const SITE_URL = "https://garantiasextraordinarias.com";
const DEFAULT_OG = "https://garantiasextraordinarias.com/images/libro-portada.jpeg";

type Props = {
  title: string;
  description: string;
  /** Path starting with "/" (e.g. "/blog"). Will be joined with SITE_URL. */
  path: string;
  image?: string;
  type?: "website" | "article" | "book" | "profile";
  locale?: "es" | "en";
  noindex?: boolean;
  jsonLd?: Record<string, unknown>[];
};

/**
 * Updates the document <head> on client-side navigation.
 * The server still injects the initial SEO tags (that's what crawlers see);
 * this component keeps the browser tab title / share buttons in sync as the
 * user navigates within the SPA.
 */
export default function Seo({
  title,
  description,
  path,
  image,
  type = "website",
  locale = "es",
  noindex = false,
  jsonLd,
}: Props) {
  useEffect(() => {
    const canonical = `${SITE_URL}${path}`;
    const ogImage = image || DEFAULT_OG;

    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex,follow" : "index,follow,max-image-preview:large");
    setLink("canonical", canonical);
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    const pair = LANGUAGE_PAIRS.find(pair => pair.some(p => p === path));
    if (pair) for (const [lang, href] of [["es", pair[0]], ["en", pair[1]], ["x-default", pair[0]]]) {
      const link = document.createElement("link");
      link.rel = "alternate"; link.hreflang = lang; link.href = SITE_URL + href;
      document.head.appendChild(link);
    }

    setMeta("property", "og:type", type);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:locale", locale === "en" ? "en_US" : "es_MX");

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);

    document.documentElement.lang = locale;

    // JSON-LD: replace any previously injected scripts marked by data-seo="dynamic"
    removeDynamicJsonLd();
    if (jsonLd && jsonLd.length) {
      for (const obj of jsonLd) {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.dataset.seo = "dynamic";
        s.textContent = JSON.stringify(obj).replace(/</g, "\\u003c");
        document.head.appendChild(s);
      }
    }

    return () => removeDynamicJsonLd();
  }, [title, description, path, image, type, locale, noindex, jsonLd]);

  return null;
}

function setMeta(attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeDynamicJsonLd() {
  document.head.querySelectorAll('script[type="application/ld+json"][data-seo="dynamic"]').forEach((n) => n.remove());
}
